<!--
  @component Skeleton — content-shaped loading placeholder.
  tier:      primitive (face-agnostic)

  A calm shimmer used while data is in flight (WS connecting, first
  fetch pending). Distinct from AbsentState, which is for the
  genuinely-empty case. Built on the global `skeleton-shimmer`
  keyframe (Styles.svelte) + the Slate Ink --bg-hover / --bg-active
  tokens, so it tracks the active theme automatically.

  Variants:
    line   — a single text-line bar (default).
    block  — a rectangular block (cards, media).
    circle — a round avatar/icon placeholder.
    text   — `rows` stacked lines, last one short, for paragraphs.

  Respects prefers-reduced-motion (drops the shimmer animation).
-->
<script lang="ts" module>
  export interface SkeletonProps {
    variant?: "line" | "block" | "circle" | "text";
    /** CSS width (e.g. "100%", "120px"). Defaults per variant. */
    width?: string;
    /** CSS height. Defaults per variant. */
    height?: string;
    /** CSS border-radius. Defaults per variant. */
    radius?: string;
    /** For variant="text": how many lines to stack. */
    rows?: number;
    /** Gap between rows (text variant). */
    gap?: string;
  }
</script>

<script lang="ts">
  let {
    variant = "line",
    width,
    height,
    radius,
    rows = 3,
    gap = "var(--space-3)",
  }: SkeletonProps = $props();

  const DEFAULTS = {
    line:   { width: "100%", height: "12px", radius: "var(--radius-xs)" },
    block:  { width: "100%", height: "64px", radius: "var(--radius-md)" },
    circle: { width: "32px", height: "32px", radius: "var(--radius-pill)" },
    text:   { width: "100%", height: "12px", radius: "var(--radius-xs)" },
  } as const;

  const d = $derived(DEFAULTS[variant]);
</script>

{#if variant === "text"}
  <div class="sk-text" style="gap: {gap}" aria-hidden="true">
    {#each Array.from({ length: rows }) as _, i (i)}
      <span
        class="sk"
        style="
          width: {i === rows - 1 ? '58%' : (width ?? d.width)};
          height: {height ?? d.height};
          border-radius: {radius ?? d.radius};
        "
      ></span>
    {/each}
  </div>
{:else}
  <span
    class="sk"
    aria-hidden="true"
    style="
      width: {width ?? d.width};
      height: {height ?? d.height};
      border-radius: {radius ?? d.radius};
    "
  ></span>
{/if}

<style>
  .sk {
    display: block;
    position: relative;
    overflow: hidden;
    background: var(--bg-hover);
  }
  .sk::after {
    content: "";
    position: absolute;
    inset: 0;
    transform: translateX(-100%);
    background: linear-gradient(
      90deg,
      transparent 0%,
      var(--bg-active) 50%,
      transparent 100%
    );
    animation: skeleton-shimmer 1.6s ease-in-out infinite;
  }
  .sk-text {
    display: flex;
    flex-direction: column;
  }
  @media (prefers-reduced-motion: reduce) {
    .sk::after {
      animation: none;
    }
  }
</style>
